import {
  HttpErrorResponse,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from "@angular/common/http";
import { inject } from "@angular/core";
import { Location } from "@angular/common";
import { Router } from "@angular/router";
import {
  BehaviorSubject,
  catchError,
  filter,
  finalize,
  switchMap,
  take,
  throwError,
} from "rxjs";
import { AuthService } from "../modules/auth/services/auth.service";
import { UserService } from "../services/user.service";

let isRefreshing = false;
let refreshTokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(
  "",
);

const tokenFreeRoutes = [
  "/auth/login",
  "/auth/token/refresh",
  "/auth/password/forgot",
  "/auth/password/reset",
  "geoapify",
];

export const tokenInterceptor: HttpInterceptorFn = (req, next) => {
  // Login call doesn't need Authorization header so we are skipping it
  if (tokenFreeRoutes.some((route) => req.url.includes(route))) {
    return next(req);
  }
  const authService = inject(AuthService);
  const userService = inject(UserService);
  const router = inject(Router);
  const location = inject(Location);

  const token = authService.getAccessToken();
  let authReq = req;

  if (token) {
    authReq = addTokenHeader(req, token);
  }

  return next(authReq).pipe(
    catchError((err) => {
      if (err instanceof HttpErrorResponse && err.status === 401) {
        return handle401Error(req, next, authService, router, userService, location);
      }

      return throwError(() => err);
    }),
  );
};

function addTokenHeader(request: HttpRequest<any>, token: string) {
  return request.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
    },
  });
}

function handle401Error(
  request: HttpRequest<any>,
  next: HttpHandlerFn,
  authService: AuthService,
  router: Router,
  userService: UserService,
  location: Location,
) {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next("");

    return authService.refreshAccessToken().pipe(
      switchMap(() => {
        const newToken = authService.getAccessToken();
        if (newToken) {
          refreshTokenSubject.next(newToken);
          return next(addTokenHeader(request, newToken));
        }
        return throwError(() => new Error("No new token found"));
      }),
      catchError((err) => {
        userService.currentUser.set(null);
        // Only redirect to login if not already on an auth-related route
        const currentPath = location.path();
        const isAuthRoute = currentPath.startsWith("/auth");
        if (!isAuthRoute) {
          router.navigate(["/auth"]);
        }
        isRefreshing = false;
        return throwError(() => err);
      }),
      finalize(() => {
        isRefreshing = false;
      }),
    );
  } else {
    return refreshTokenSubject.pipe(
      filter((token) => token !== ""),
      take(1),
      switchMap((token) => next(addTokenHeader(request, token!))),
    );
  }
}
